/**
 * Restaurant website visual templates.
 *
 * Each template defines a complete set of design tokens that drives the
 * typography, colour palette, hero treatment, card styles, and spacing rhythm
 * of the restaurant landing pages.  The active template is selected via the
 * `site_template` AppConfig locale key (e.g. "ember", "garden", "bold",
 * "classic").  Changing the key in the admin portal re-skins the whole site
 * without a code deploy.
 *
 * All contrast ratios between foreground text and the declared backgrounds
 * meet WCAG AA (≥ 4.5 : 1 for normal text, ≥ 3 : 1 for large text).
 */

export const TEMPLATE_NAMES = {
  classic: 'classic',
  ember: 'ember',
  garden: 'garden',
  bold: 'bold',
  // Full-page template families — each renders its own complete layout.
  duskMidnight: 'dusk-midnight',
  duskBordeaux: 'dusk-bordeaux',
  bloomLinen: 'bloom-linen',
  bloomCoastal: 'bloom-coastal',
  craftCarbon: 'craft-carbon',
  craftClay: 'craft-clay',
};

const TEMPLATES = {
  /**
   * Classic — refined, timeless.
   * Warm white canvas, muted-gold accent, elegant Cormorant Garamond heading
   * paired with Inter body.  Closely resembles the current design, polished.
   */
  classic: {
    name: 'classic',
    label: 'Classic',
    description:
      'Refined and timeless — warm whites, muted gold, elegant serif headings.',
    // Colour palette
    background: '#FFFDF8',
    backgroundAlt: '#F5F3EE',
    surface: '#FFFFFF',
    primary: '#8B7355',
    primaryText: '#FFFFFF',
    text: '#1C1C1C',
    textSecondary: '#6B6B6B',
    // Typography
    headingFont: '"Cormorant Garamond", Georgia, serif',
    bodyFont: '"Inter", "Helvetica Neue", Arial, sans-serif',
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Inter:wght@400;500&display=swap',
    // Hero overlay
    heroOverlay:
      'linear-gradient(0deg, rgba(0,0,0,0.55) 25%, rgba(0,0,0,0.00) 75%)',
    // Shape / spacing
    borderRadius: '8px',
    cardBorderRadius: '12px',
    buttonBorderRadius: '6px',
    // Card surface
    cardBackground: '#FFFFFF',
    cardBoxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },

  /**
   * Ember — dark, moody, dramatic.
   * Near-black backgrounds, warm amber accent, bold Playfair Display headings,
   * high-contrast white body text.  Hero uses a heavy dark gradient from bottom.
   * Sections alternate between near-black and deep charcoal.
   */
  ember: {
    name: 'ember',
    label: 'Ember',
    description:
      'Dark and dramatic — amber accents, bold serif headings, deep charcoal backgrounds.',
    background: '#111111',
    backgroundAlt: '#1C1C1C',
    surface: '#1C1C1C',
    primary: '#F59E0B',
    primaryText: '#000000',
    text: '#FFFFFF',
    textSecondary: '#D1D5DB',
    headingFont: '"Playfair Display", "Times New Roman", serif',
    bodyFont: '"Inter", "Helvetica Neue", Arial, sans-serif',
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@400;500&display=swap',
    heroOverlay:
      'linear-gradient(0deg, rgba(0,0,0,0.90) 30%, rgba(0,0,0,0.15) 100%)',
    borderRadius: '4px',
    cardBorderRadius: '4px',
    buttonBorderRadius: '2px',
    cardBackground: '#1C1C1C',
    cardBoxShadow: '0 4px 24px rgba(0,0,0,0.5)',
  },

  /**
   * Garden — light, airy, editorial.
   * Off-white canvas, sage-green accent, humanist DM Sans throughout.
   * Generous whitespace; hero uses a soft white-to-transparent gradient.
   * Section backgrounds alternate cream and white.
   */
  garden: {
    name: 'garden',
    label: 'Garden',
    description:
      'Light and airy — sage greens, generous whitespace, humanist sans typography.',
    background: '#FAFAF7',
    backgroundAlt: '#F0EDE6',
    surface: '#FFFFFF',
    primary: '#4A7C59',
    primaryText: '#FFFFFF',
    text: '#2D2D2D',
    textSecondary: '#6B7280',
    headingFont: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    bodyFont: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,700&display=swap',
    heroOverlay:
      'linear-gradient(180deg, rgba(255,255,255,0.00) 50%, rgba(240,237,230,0.70) 100%)',
    borderRadius: '16px',
    cardBorderRadius: '20px',
    buttonBorderRadius: '100px',
    cardBackground: '#FFFFFF',
    cardBoxShadow: '0 1px 8px rgba(0,0,0,0.06)',
  },

  /**
   * Bold — high-energy, graphic, high-contrast.
   * Pure white background, vivid accent pulled from the restaurant's brand,
   * geometric Space Grotesk throughout.  Cards use thick borders and drop
   * shadows; hero uses a bold full-width text block with a coloured underline.
   */
  bold: {
    name: 'bold',
    label: 'Bold',
    description:
      'High-energy and graphic — vivid brand-colour accent, geometric sans, strong shadows.',
    background: '#FFFFFF',
    backgroundAlt: '#F4F4F4',
    surface: '#FFFFFF',
    primary: '#E63012',
    primaryText: '#FFFFFF',
    text: '#000000',
    textSecondary: '#444444',
    headingFont: '"Space Grotesk", "Helvetica Neue", Arial, sans-serif',
    bodyFont: '"Space Grotesk", "Helvetica Neue", Arial, sans-serif',
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&display=swap',
    heroOverlay:
      'linear-gradient(0deg, rgba(0,0,0,0.65) 20%, rgba(0,0,0,0.00) 70%)',
    borderRadius: '0px',
    cardBorderRadius: '0px',
    buttonBorderRadius: '0px',
    cardBackground: '#FFFFFF',
    cardBoxShadow: '4px 4px 0px 0px #000000',
  },
};

/**
 * Full-page templates — each renders its own complete page layout (nav, hero,
 * sections, footer) instead of using the shared token-based component tree.
 *
 * `renderMode: 'full-page'` is the signal RestaurantMainView checks to
 * short-circuit to FullPageTemplateRenderer.  The token fields (background,
 * primary, etc.) are used for the picker card preview in Kudoz; they are NOT
 * injected into the consumer page by useRestaurantTemplate.
 *
 * No `googleFontsUrl` — each full-page component loads its own fonts inline.
 */
const FULL_PAGE_TEMPLATES = {
  /**
   * Dusk Midnight — deep charcoal, warm amber, Instrument Serif headings.
   * Dark, atmospheric, best for evening-only venues.
   */
  'dusk-midnight': {
    name: 'dusk-midnight',
    label: 'Dusk Midnight',
    description:
      'Deep & atmospheric — charcoal canvas, warm amber, serif headings.',
    renderMode: 'full-page',
    background: '#171615',
    backgroundAlt: '#201f1c',
    surface: '#201f1c',
    primary: '#d6a958',
    primaryText: '#171615',
    text: '#eee7d8',
    textSecondary: '#e3dbc9',
    headingFont: '"Instrument Serif", Georgia, serif',
    bodyFont: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    borderRadius: '0px',
    cardBorderRadius: '0px',
    buttonBorderRadius: '0px',
    cardBackground: '#201f1c',
    cardBoxShadow: 'none',
    heroOverlay:
      'linear-gradient(90deg, rgba(18,18,17,0.95) 0%, rgba(23,22,21,0.45) 50%, rgba(23,22,21,0.20) 100%)',
  },

  /**
   * Dusk Bordeaux — deep burgundy, warm gold, Playfair Display headings.
   * Rich, romantic, old-world European feel.
   */
  'dusk-bordeaux': {
    name: 'dusk-bordeaux',
    label: 'Dusk Bordeaux',
    description:
      'Rich & romantic — burgundy, gold, old-world European character.',
    renderMode: 'full-page',
    background: '#4b1725',
    backgroundAlt: '#32101b',
    surface: '#32101b',
    primary: '#d9aa67',
    primaryText: '#4b1725',
    text: '#f1e6d1',
    textSecondary: '#f0ddbb',
    headingFont: '"Playfair Display", Georgia, serif',
    bodyFont: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    borderRadius: '0px',
    cardBorderRadius: '0px',
    buttonBorderRadius: '0px',
    cardBackground: '#32101b',
    cardBoxShadow: 'none',
    heroOverlay:
      'linear-gradient(90deg, rgba(61,16,30,0.95) 0%, rgba(75,23,37,0.45) 50%, rgba(75,23,37,0.20) 100%)',
  },

  /**
   * Bloom Linen — warm cream, muted sage, Fraunces serif.
   * Airy, artisan, suited to cafés and daytime venues.
   */
  'bloom-linen': {
    name: 'bloom-linen',
    label: 'Bloom Linen',
    description:
      'Warm & artisan — cream canvas, sage green, organic serif typography.',
    renderMode: 'full-page',
    background: '#f5f0e6',
    backgroundAlt: '#fbf8f1',
    surface: '#fbf8f1',
    primary: '#697d62',
    primaryText: '#f5f0e6',
    text: '#2c362d',
    textSecondary: '#667067',
    headingFont: 'Fraunces, Georgia, serif',
    bodyFont: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    borderRadius: '4px',
    cardBorderRadius: '4px',
    buttonBorderRadius: '0px',
    cardBackground: '#fbf8f1',
    cardBoxShadow: 'none',
    heroOverlay: 'none',
  },

  /**
   * Bloom Coastal — fresh white, deep teal, Instrument Serif.
   * Clean, coastal, light-filled seafood / contemporary feel.
   */
  'bloom-coastal': {
    name: 'bloom-coastal',
    label: 'Bloom Coastal',
    description:
      'Fresh & coastal — white canvas, deep teal, clean editorial layout.',
    renderMode: 'full-page',
    background: '#fbfcf9',
    backgroundAlt: '#e8f3f1',
    surface: '#fbfcf9',
    primary: '#075c68',
    primaryText: '#ffffff',
    text: '#123d44',
    textSecondary: '#587078',
    headingFont: '"Instrument Serif", Georgia, serif',
    bodyFont: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    borderRadius: '0px',
    cardBorderRadius: '0px',
    buttonBorderRadius: '0px',
    cardBackground: '#fbfcf9',
    cardBoxShadow: 'none',
    heroOverlay:
      'linear-gradient(0deg, rgba(18,61,68,0.45) 0%, transparent 60%)',
  },

  /**
   * Craft Carbon — near-black, acid lime, Space Mono.
   * Bold, industrial, graphic. Ramen bars, urban kitchens.
   */
  'craft-carbon': {
    name: 'craft-carbon',
    label: 'Craft Carbon',
    description:
      'Bold & industrial — near-black, acid yellow, monospace typography.',
    renderMode: 'full-page',
    background: '#111211',
    backgroundAlt: '#1a1c1b',
    surface: '#1a1c1b',
    primary: '#d7f42d',
    primaryText: '#111211',
    text: '#f2f1e9',
    textSecondary: '#a9aaa0',
    headingFont: '"Barlow Condensed", "Arial Black", sans-serif',
    bodyFont: '"Space Mono", monospace',
    borderRadius: '0px',
    cardBorderRadius: '0px',
    buttonBorderRadius: '0px',
    cardBackground: '#1a1c1b',
    cardBoxShadow: 'none',
    heroOverlay:
      'linear-gradient(90deg, rgba(17,18,17,0.32) 0%, transparent 45%), linear-gradient(0deg, rgba(17,18,17,0.35) 0%, transparent 45%)',
  },

  /**
   * Craft Clay — dark charcoal, warm terracotta, DM Mono + Fraunces.
   * Earthy, rustic, tactile. Taquerias, wood-fired venues.
   */
  'craft-clay': {
    name: 'craft-clay',
    label: 'Craft Clay',
    description: 'Earthy & rustic — charcoal, terracotta, textured warmth.',
    renderMode: 'full-page',
    background: '#292522',
    backgroundAlt: '#3a332d',
    surface: '#3a332d',
    primary: '#b65f45',
    primaryText: '#eee3d1',
    text: '#eee3d1',
    textSecondary: '#b3a493',
    headingFont: 'Fraunces, Georgia, serif',
    bodyFont: '"DM Mono", monospace',
    borderRadius: '0px',
    cardBorderRadius: '0px',
    buttonBorderRadius: '0px',
    cardBackground: '#3a332d',
    cardBoxShadow: 'none',
    heroOverlay: 'none',
  },
};

Object.assign(TEMPLATES, FULL_PAGE_TEMPLATES);

export default TEMPLATES;
